import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	FOCUS_AREA_DERIVED_STATUS_KEY_TO_COLOR_MAP,
	FOCUS_AREA_DERIVED_STATUS_KEY_TO_LABEL_MAP,
	FOCUS_AREA_EMPTY_HEALTH_ICON_COLOR,
	FOCUS_AREA_HEALTH_KEYS_LIST,
	FOCUS_AREA_STATUS_HEALTH_UI_SEPARATION_FEATURE_GATE,
	FOCUS_AREA_STATUS_KEYS,
	FOCUS_AREA_STATUS_KEYS_LIST,
	type ConfiguredFocusAreaStatus,
	type FocusAreaDerivedStatusKey,
	type FocusAreaDerivedStatusLabel,
	type FocusAreaHealthKey,
	type FocusAreaIconStatus,
	type FocusAreaStatusKey,
} from './types';

type FocusAreaDerivedStatus = {
	derivedStatusKey: FocusAreaDerivedStatusKey;
	derivedStatusLabel: FocusAreaDerivedStatusLabel;
};

type ConfiguredFocusAreaDerivedStatus = {
	derivedStatusKey: string;
	/** Undefined for a configured status, which this package has no label for. */
	derivedStatusLabel: string | undefined;
};

type GetDerivedStatusArgs = {
	status?: FocusAreaIconStatus | undefined;
	health?: FocusAreaHealthKey | undefined;
};

export const getFocusAreaIconColor = ({
	customColor,
	status,
	health,
}: {
	customColor?: NewCoreIconProps['color'];
	status?: FocusAreaIconStatus | undefined;
	health?: FocusAreaHealthKey | undefined;
}): NewCoreIconProps['color'] => {
	if (customColor) {
		return customColor;
	}

	if (fg(FOCUS_AREA_STATUS_HEALTH_UI_SEPARATION_FEATURE_GATE)) {
		const healthKey = getValidHealth(health);
		return healthKey
			? FOCUS_AREA_DERIVED_STATUS_KEY_TO_COLOR_MAP[healthKey]
			: FOCUS_AREA_EMPTY_HEALTH_ICON_COLOR;
	}

	const { derivedStatusKey } = getDerivedStatusInternal({ status, health });
	return isDerivedStatusKey(derivedStatusKey)
		? FOCUS_AREA_DERIVED_STATUS_KEY_TO_COLOR_MAP[derivedStatusKey]
		: FOCUS_AREA_EMPTY_HEALTH_ICON_COLOR;
};

export function getDerivedStatus({
	status,
	health,
}: {
	status?: FocusAreaStatusKey | undefined;
	health?: FocusAreaHealthKey | undefined;
}): FocusAreaDerivedStatus;
export function getDerivedStatus({
	status,
	health,
}: {
	status: ConfiguredFocusAreaStatus;
	health?: FocusAreaHealthKey | undefined;
}): ConfiguredFocusAreaDerivedStatus;
export function getDerivedStatus({
	status,
	health,
}: GetDerivedStatusArgs): ConfiguredFocusAreaDerivedStatus;
export function getDerivedStatus({
	status,
	health,
}: GetDerivedStatusArgs): ConfiguredFocusAreaDerivedStatus {
	return getDerivedStatusInternal({ status, health });
}

const getDerivedStatusInternal = ({
	status,
	health,
}: GetDerivedStatusArgs): ConfiguredFocusAreaDerivedStatus => {
	const validStatus = getValidStatus(status);
	const healthKey = getValidHealth(health);
	const derivedStatusKey =
		isInProgressStatus(validStatus) && healthKey ? healthKey : getStatusKey(validStatus);

	return {
		derivedStatusKey,
		// Only legacy keys have a label in this package. A configured status is named by
		// the workspace, so there is no label to return for it and we must not fall back
		// to the raw key: that would hand consumers a slug to render.
		derivedStatusLabel: isDerivedStatusKey(derivedStatusKey)
			? FOCUS_AREA_DERIVED_STATUS_KEY_TO_LABEL_MAP[derivedStatusKey]
			: undefined,
	};
};

const getValidStatus = (status?: FocusAreaIconStatus | undefined): FocusAreaIconStatus => {
	if (isConfiguredStatus(status)) {
		return status;
	}

	return status && FOCUS_AREA_STATUS_KEYS_LIST.includes(status)
		? status
		: FOCUS_AREA_STATUS_KEYS.PENDING;
};

const isConfiguredStatus = (
	status: FocusAreaIconStatus | undefined,
): status is ConfiguredFocusAreaStatus =>
	typeof status === 'object' && status !== null && typeof status.key === 'string';

/**
 * Narrows a possibly configured status key to one this package knows about.
 *
 * Both derived status maps are declared `satisfies Record<FocusAreaDerivedStatusKey, ...>`,
 * so they share this key set and this guard is valid for looking up either one.
 */
const isDerivedStatusKey = (key: string): key is FocusAreaDerivedStatusKey =>
	key in FOCUS_AREA_DERIVED_STATUS_KEY_TO_LABEL_MAP;

const isInProgressStatus = (status: FocusAreaIconStatus): boolean =>
	status === FOCUS_AREA_STATUS_KEYS.IN_PROGRESS ||
	(isConfiguredStatus(status) && status.category === 'IN_PROGRESS');

const getStatusKey = (status: FocusAreaIconStatus): string =>
	isConfiguredStatus(status) ? status.key : status;

const getValidHealth = (health?: FocusAreaHealthKey | undefined): FocusAreaHealthKey | undefined =>
	health && FOCUS_AREA_HEALTH_KEYS_LIST.includes(health) ? health : undefined;
